import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabase } from '../../../supabaseClient';
import toast from 'react-hot-toast';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ServiceForm = ({ navigate, id, onSuccess, isModal = false }) => {
  // Déplacez les hooks au début du composant
  const params = useParams();
  const routeNavigate = useNavigate();

  // Utilisez routeNavigate si navigate n'est pas fourni
  const navFunction = navigate || routeNavigate;

  // Utilisez l'ID passé en prop ou de l'URL
  const serviceId = id || params.id;

  const isEditMode = !!serviceId;
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Définition de la toolbar et des formats pour ReactQuill
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  const formats = [
    'header',
    'bold', 
    'italic', 
    'underline', 
    'strike',
    'list', 
    'bullet',
    'link'
  ];

  // Gestion du changement de la description (ReactQuill)
  const handleDescriptionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      description: value
    }));
  };
  
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
  const fetchService = async () => {
    if (isEditMode) {
      setLoading(true);
      const { data, error } = await getSupabase()
        .from('services')
        .select('name, description')
        .eq('id', serviceId)
        .single();
      if (error) {
        setError("Impossible de charger le service.");
        setLoading(false);
        return;
      }
      setFormData({
        name: data?.name || '',
        description: data?.description || ''
      });
      setLoading(false);
    }
  };
  fetchService();
}, [serviceId, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Identifiant de chargement pour le toast
    const loadingToast = toast.loading(isEditMode ? 'Mise à jour du service...' : 'Création du service...');
    
    try {
      // Validation
      if (!formData.name) {
        toast.error('Le nom du service est obligatoire', { id: loadingToast });
        return;
      }
      
      const supabase = getSupabase();
      
      // Requête d'insertion ou de mise à jour
      const { data: serviceData, error } = isEditMode
        ? await supabase
            .from('services')
            .update(formData)
            .eq('id', serviceId)
        : await supabase
            .from('services')
            .insert(formData)
            .select();
      
      // Utiliser serviceData si nécessaire
      if (serviceData && serviceData.length > 0) {
        console.log('Service créé/mis à jour:', serviceData[0]);
      }
      
      // Gestion des erreurs
      if (error) {
        throw error;
      }
      
      // Notification de succès
      toast.success(
        isEditMode 
          ? 'Service mis à jour avec succès' 
          : `Service "${formData.name}" créé avec succès`, 
        { id: loadingToast }
      );
      
      // Appeler onSuccess si fourni, sinon rediriger
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          if (!isModal) {
            navFunction('/admin/services');
          }
        }
      }, 1000);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du service:', error);
      toast.error(`Erreur : ${error.message}`, { id: loadingToast });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
          {success}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="max-w-xl mx-auto bg-white rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Nom du service
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              placeholder="Saisissez le nom du service"
              className="form-input"
              value={formData.name}
              onChange={handleChange}
              maxLength="255"
            />
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <ReactQuill
              id="description"
              theme="snow"
              value={formData.description}
              onChange={handleDescriptionChange}
              modules={modules}
              formats={formats}
              placeholder="Décrivez brièvement le service"
              className="bg-white"
            />
          </div>
        </div>
        
        <div className="px-6 py-4 flex justify-between space-x-3">
          <button
            type="button"
            onClick={() => {
              if (onSuccess) {
                onSuccess();
              } else {
                navFunction('/admin/services');
              }
            }}
            className="btn-secondary"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn-primary"
          >
            {isEditMode ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
};

ServiceForm.propTypes = {
  navigate: PropTypes.func,
  id: PropTypes.string,
  onSuccess: PropTypes.func,
  isModal: PropTypes.bool
};

export default ServiceForm;
